import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useForm, Controller, FieldError } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import {
  TextInput,
  Combobox,
  Option,
  Button,
  ButtonSize,
  ButtonColor,
  Loader,
} from '@code/innovance-react-ui-kit';
import styled from 'styled-components';

import {
  Customer,
  CustomerContactInfo,
  LoanContactPageValidation,
} from 'src/front/api/apiInstance';
import {
  ReferenceDataType,
  CustomerContactInfoFormatResponse,
  PhoneType,
} from 'src/front/api/generated';

import {
  formatNumeric,
  formatEmail,
  dictionaryLineFormat,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import usePRM from 'src/front/common/hooks/usePRM';

import { BREAK_POINTS } from 'src/front/constant';
import { useCustomerSearchState } from 'src/front/contexts/LoanApplicationContext';
import {
  FormElement,
  Container,
  Title,
  Subtitle,
  Form,
  FormRow,
  Separator,
} from 'src/front/components/FormStyles';
import { LoanPhoneEmailSchema } from 'src/front/common/validations/loanApplication';

export const FlexFormElement = styled(FormElement)`
  display: flex;
  justify-content: space-between;
`;

export const AreaCode = styled.div`
  width: 39%;

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) {
    width: 36%;
  }
`;

export const PhoneNumber = styled.div`
  width: 59%;

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) {
    width: 62%;
  }
`;

export interface PhoneValidations {
  mobilePhone?: CustomerContactInfoFormatResponse;
  actualLivingAddressPhone?: CustomerContactInfoFormatResponse;
  workPhone?: CustomerContactInfoFormatResponse;
  contactPersonMobilePhone1?: CustomerContactInfoFormatResponse;
  contactPersonMobilePhone2?: CustomerContactInfoFormatResponse;
  registrationAddressPhone?: CustomerContactInfoFormatResponse;
}

interface FormData {
  emailAddress: string;
  mobilePhoneCountry: Option | null;
  mobilePhoneFieldCode: string;
  mobilePhoneTelephoneNo: string;
  actualLivingAddressPhoneCountry: Option | null;
  actualLivingAddressPhoneFieldCode: string;
  actualLivingAddressPhoneTelephoneNo: string;
  registrationAddressPhoneCountry: Option | null;
  registrationAddressPhoneFieldCode: string;
  registrationAddressPhoneTelephoneNo: string;
  workPhoneCountry: Option | null;
  workPhoneFieldCode: string;
  workPhoneTelephoneNo: string;
  contactPersonMobilePhone1Country: Option | null;
  contactPersonMobilePhone1FieldCode: string;
  contactPersonMobilePhone1TelephoneNo: string;
  contactPersonMobilePhone2Country: Option | null;
  contactPersonMobilePhone2FieldCode: string;
  contactPersonMobilePhone2TelephoneNo: string;
}

const defaultValues = {
  emailAddress: '',
  mobilePhoneCountry: null,
  mobilePhoneFieldCode: '',
  mobilePhoneTelephoneNo: '',
  actualLivingAddressPhoneCountry: null,
  actualLivingAddressPhoneFieldCode: '',
  actualLivingAddressPhoneTelephoneNo: '',
  workPhoneCountry: null,
  workPhoneFieldCode: '',
  workPhoneTelephoneNo: '',
  registrationAddressPhoneCountry: null,
  registrationAddressPhoneFieldCode: '',
  registrationAddressPhoneTelephoneNo: '',
  contactPersonMobilePhone1Country: null,
  contactPersonMobilePhone1FieldCode: '',
  contactPersonMobilePhone1TelephoneNo: '',
  contactPersonMobilePhone2Country: null,
  contactPersonMobilePhone2FieldCode: '',
  contactPersonMobilePhone2TelephoneNo: '',
};

interface Props {
  goToNextStep: () => void;
}

const PhoneEmailEntry: FC<Props> = ({ goToNextStep }) => {
  const { t } = useTranslation();
  const { customerId, isNewCustomer } = useCustomerSearchState();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [formValidations, setFormValidations] = useState<PhoneValidations>();
  const countryPhoneCodes = usePRM(ReferenceDataType.COUNTRYCODETELEPHONE);

  const { handleSubmit, errors, setValue, control, watch, formState } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(LoanPhoneEmailSchema, {
      abortEarly: false,
      context: { formValidations },
    }),
  });

  const loadPhoneEmailInformation = useCallback(async () => {
    const { data } = await LoanContactPageValidation.getLoanContactInfoPageData();
    if (data.result === 'OK' && data.pageData) {
      const {
        emailAddress,
        mobilePhone,
        actualLivingAddressPhone,
        workPhone,
        contactPersonMobilePhone1,
        contactPersonMobilePhone2,
        registrationAddressPhone,
      } = data.pageData;
      [
        { name: 'emailAddress', value: emailAddress },
        { name: 'mobilePhoneCountry', value: optionFormat(mobilePhone.countryCode) },
        { name: 'mobilePhoneFieldCode', value: mobilePhone.fieldCode },
        { name: 'mobilePhoneTelephoneNo', value: mobilePhone.telephoneNo },
        ...(actualLivingAddressPhone
          ? [
              {
                name: 'actualLivingAddressPhoneCountry',
                value: optionFormat(actualLivingAddressPhone.countryCode),
              },
              {
                name: 'actualLivingAddressPhoneFieldCode',
                value: actualLivingAddressPhone.fieldCode,
              },
              {
                name: 'actualLivingAddressPhoneTelephoneNo',
                value: actualLivingAddressPhone.telephoneNo,
              },
            ]
          : []),
        ...(workPhone
          ? [
              {
                name: 'workPhoneCountry',
                value: optionFormat(workPhone.countryCode),
              },
              {
                name: 'workPhoneFieldCode',
                value: workPhone.fieldCode,
              },
              {
                name: 'workPhoneTelephoneNo',
                value: workPhone.telephoneNo,
              },
            ]
          : []),
        ...(registrationAddressPhone
          ? [
              {
                name: 'registrationAddressPhoneCountry',
                value: optionFormat(registrationAddressPhone.countryCode),
              },
              {
                name: 'registrationAddressPhoneFieldCode',
                value: registrationAddressPhone.fieldCode,
              },
              {
                name: 'registrationAddressPhoneTelephoneNo',
                value: registrationAddressPhone.telephoneNo,
              },
            ]
          : []),
        ...(contactPersonMobilePhone1
          ? [
              {
                name: 'contactPersonMobilePhone1Country',
                value: optionFormat(contactPersonMobilePhone1.countryCode),
              },
              {
                name: 'contactPersonMobilePhone1FieldCode',
                value: contactPersonMobilePhone1.fieldCode,
              },
              {
                name: 'contactPersonMobilePhone1TelephoneNo',
                value: contactPersonMobilePhone1.telephoneNo,
              },
            ]
          : []),

        ...(contactPersonMobilePhone2
          ? [
              {
                name: 'contactPersonMobilePhone2Country',
                value: optionFormat(contactPersonMobilePhone2.countryCode),
              },
              {
                name: 'contactPersonMobilePhone2FieldCode',
                value: contactPersonMobilePhone2.fieldCode,
              },
              {
                name: 'contactPersonMobilePhone2TelephoneNo',
                value: contactPersonMobilePhone2.telephoneNo,
              },
            ]
          : []),
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

      // Acquire validations
      mobilePhone.countryCode &&
        handleCountryChange(
          'mobilePhone',
          PhoneType.MOBILEPHONE
        )(optionFormat(mobilePhone.countryCode));
      actualLivingAddressPhone?.countryCode &&
        handleCountryChange(
          'actualLivingAddressPhone',
          PhoneType.HOMEPHONE
        )(optionFormat(actualLivingAddressPhone.countryCode));
      workPhone?.countryCode &&
        handleCountryChange('workPhone', PhoneType.WORKPHONE)(optionFormat(workPhone.countryCode));
      registrationAddressPhone?.countryCode &&
        handleCountryChange(
          'registrationAddressPhone',
          PhoneType.REGISTRATIONADDRESSPHONE
        )(optionFormat(registrationAddressPhone.countryCode));
      contactPersonMobilePhone1?.countryCode &&
        handleCountryChange(
          'contactPersonMobilePhone1',
          PhoneType.CONTACTMOBILEPHONE
        )(optionFormat(contactPersonMobilePhone1.countryCode));
      contactPersonMobilePhone2?.countryCode &&
        handleCountryChange(
          'contactPersonMobilePhone2',
          PhoneType.CONTACTMOBILEPHONE
        )(optionFormat(contactPersonMobilePhone2.countryCode));
    } else {
      const { data: response } = await Customer.getCustomerLoanContactInfo({
        customerId: customerId || '',
      });

      const mobilPhoneCountry = countryPhoneCodes.find(
        (country) => country.value === response.mobilePhone?.countryCode
      );
      const workPhoneCountry = countryPhoneCodes.find(
        (country) => country.value === response.workPhone?.countryCode
      );
      const actualLivingAddressPhoneCountry =
        countryPhoneCodes.find(
          (country) => country.value === response.actualLivingAddressPhone?.countryCode
        ) || null;
      const contactPersonMobilPhone1Country =
        countryPhoneCodes.find(
          (country) => country.value === response.contactPersonMobilePhone1?.countryCode
        ) || null;
      const contactPersonMobilPhone2Country =
        countryPhoneCodes.find(
          (country) => country.value === response.contactPersonMobilePhone2?.countryCode
        ) || null;
      const registrationAddressCountry =
        countryPhoneCodes.find(
          (country) => country.value === response.contactPersonMobilePhone2?.countryCode
        ) || null;
      [
        { name: 'emailAddress', value: response.emailAddress },
        { name: 'mobilePhoneCountry', value: mobilPhoneCountry },
        ...(response.mobilePhone?.fieldCode && response.mobilePhone?.telephoneNo
          ? [
              {
                name: 'mobilePhoneFieldCode',
                value: response.mobilePhone?.fieldCode,
              },
              {
                name: 'mobilePhoneTelephoneNo',
                value: response.mobilePhone?.telephoneNo,
              },
            ]
          : []),
        { name: 'actualLivingAddressPhoneCountry', value: actualLivingAddressPhoneCountry },
        ...(response.actualLivingAddressPhone?.fieldCode &&
        response.actualLivingAddressPhone?.telephoneNo
          ? [
              {
                name: 'actualLivingAddressPhoneFieldCode',
                value: response.actualLivingAddressPhone.fieldCode,
              },
              {
                name: 'actualLivingAddressPhoneTelephoneNo',
                value: response.actualLivingAddressPhone.telephoneNo,
              },
            ]
          : []),
        { name: 'workPhoneCountry', value: workPhoneCountry },
        ...(response.workPhone?.fieldCode && response.workPhone?.telephoneNo
          ? [
              {
                name: 'workPhoneFieldCode',
                value: response.workPhone.fieldCode,
              },
              {
                name: 'workPhoneTelephoneNo',
                value: response.workPhone.telephoneNo,
              },
            ]
          : []),
        { name: 'registrationAddressPhoneCountry', value: registrationAddressCountry },
        ...(response.registrationAddressPhone?.fieldCode &&
        response.registrationAddressPhone?.telephoneNo
          ? [
              {
                name: 'registrationAddressPhoneFieldCode',
                value: response.registrationAddressPhone.fieldCode,
              },
              {
                name: 'registrationAddressPhoneTelephoneNo',
                value: response.registrationAddressPhone.telephoneNo,
              },
            ]
          : []),
        { name: 'contactPersonMobilePhone1Country', value: contactPersonMobilPhone1Country || '' },
        ...(response.contactPersonMobilePhone1?.fieldCode &&
        response.contactPersonMobilePhone1?.telephoneNo
          ? [
              {
                name: 'contactPersonMobilePhone1FieldCode',
                value: response.contactPersonMobilePhone1?.fieldCode || '',
              },
              {
                name: 'contactPersonMobilePhone1TelephoneNo',
                value: response.contactPersonMobilePhone1?.telephoneNo || '',
              },
            ]
          : []),

        ...(response.contactPersonMobilePhone2?.fieldCode &&
        response.contactPersonMobilePhone2?.telephoneNo
          ? [
              { name: 'contactPersonMobilePhone2Country', value: contactPersonMobilPhone2Country },
              {
                name: 'contactPersonMobilePhon2FieldCode',
                value: response.contactPersonMobilePhone2.fieldCode,
              },
              {
                name: 'contactPersonMobilePhone2TelephoneNo',
                value: response.contactPersonMobilePhone2.telephoneNo,
              },
            ]
          : []),
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

      // Acquire validations
      mobilPhoneCountry &&
        handleCountryChange('mobilePhone', PhoneType.MOBILEPHONE)(mobilPhoneCountry);
      actualLivingAddressPhoneCountry &&
        handleCountryChange(
          'actualLivingAddressPhone',
          PhoneType.HOMEPHONE
        )(actualLivingAddressPhoneCountry);
      registrationAddressCountry &&
        handleCountryChange(
          'registrationAddressPhone',
          PhoneType.REGISTRATIONADDRESSPHONE
        )(registrationAddressCountry);
      workPhoneCountry && handleCountryChange('workPhone', PhoneType.WORKPHONE)(workPhoneCountry);
      contactPersonMobilPhone1Country &&
        handleCountryChange(
          'contactPersonMobilePhone1',
          PhoneType.CONTACTMOBILEPHONE
        )(contactPersonMobilPhone1Country);

      contactPersonMobilPhone2Country &&
        handleCountryChange(
          'contactPersonMobilePhone2',
          PhoneType.CONTACTMOBILEPHONE
        )(contactPersonMobilPhone2Country);
    }
    setIsLoading(false);
  }, [countryPhoneCodes, customerId, setValue]);

  useEffect(() => {
    if (countryPhoneCodes.length) {
      loadPhoneEmailInformation();
    }
  }, [countryPhoneCodes.length, loadPhoneEmailInformation]);

  const handleCountryChange = (
    fieldName: string,
    phoneType: PhoneType,
    onChange?: (option: Option | null) => void
  ) => async (country: Option | null) => {
    onChange && onChange(country);
    if (country) {
      const { data } = await CustomerContactInfo.getCustomerContactInfoFormat({
        phoneType,
        countryCode: country?.value || '',
      });
      setFormValidations((state) => ({ ...state, [fieldName]: data }));
    } else {
      setFormValidations(undefined);
    }
  };

  const onSubmit = async (form: FormData): Promise<void> => {
    const { data } = await LoanContactPageValidation.validateLoanContactInfoPageData({
      emailAddress: form.emailAddress,
      mobilePhone: {
        countryCode: dictionaryLineFormat(form.mobilePhoneCountry),
        fieldCode: form.mobilePhoneFieldCode,
        telephoneNo: form.mobilePhoneTelephoneNo,
      },
      ...(form.actualLivingAddressPhoneCountry &&
      form.actualLivingAddressPhoneFieldCode &&
      form.actualLivingAddressPhoneTelephoneNo
        ? {
            actualLivingAddressPhone: {
              countryCode: dictionaryLineFormat(form.actualLivingAddressPhoneCountry),
              fieldCode: form.actualLivingAddressPhoneFieldCode,
              telephoneNo: form.actualLivingAddressPhoneTelephoneNo,
            },
          }
        : {}),
      ...(form.workPhoneCountry
        ? {
            workPhone: {
              countryCode: dictionaryLineFormat(form.workPhoneCountry),
              fieldCode: form.workPhoneFieldCode,
              telephoneNo: form.workPhoneTelephoneNo,
            },
          }
        : {}),
      ...(form.registrationAddressPhoneCountry
        ? {
            registrationAddressPhone: {
              countryCode: dictionaryLineFormat(form.registrationAddressPhoneCountry),
              fieldCode: form.registrationAddressPhoneFieldCode,
              telephoneNo: form.registrationAddressPhoneTelephoneNo,
            },
          }
        : {}),
      ...(form.contactPersonMobilePhone1Country &&
      form.contactPersonMobilePhone1FieldCode &&
      form.contactPersonMobilePhone1TelephoneNo
        ? {
            contactPersonMobilePhone1: {
              countryCode: dictionaryLineFormat(form.contactPersonMobilePhone1Country),
              fieldCode: form.contactPersonMobilePhone1FieldCode,
              telephoneNo: form.contactPersonMobilePhone1TelephoneNo,
            },
          }
        : {}),
      ...(form.contactPersonMobilePhone2Country &&
      form.contactPersonMobilePhone2FieldCode &&
      form.contactPersonMobilePhone2TelephoneNo
        ? {
            contactPersonMobilePhone2: {
              countryCode: dictionaryLineFormat(form.contactPersonMobilePhone2Country),
              fieldCode: form.contactPersonMobilePhone2FieldCode,
              telephoneNo: form.contactPersonMobilePhone2TelephoneNo,
            },
          }
        : {}),
    });

    if (data.result === 'OK') {
      goToNextStep();
    }
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_phoneEmailEntry_phoneEmailInformation_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="loanApp_phoneEmailEntry_email_label" />
      </Subtitle>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="emailAddress"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.emailAddress?.message as string)}
                  formatter={formatEmail}
                  label={t('loanApp_phoneEmailEntry_email_label')}
                  name="emailAddress"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="loanApp_phoneEmailEntry_mobilePhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="mobilePhoneCountry"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={!isNewCustomer}
                  errorMessage={t((errors?.mobilePhoneCountry as FieldError)?.message as string)}
                  items={countryPhoneCodes}
                  label={t('loanApp_phoneEmailEntry_countryPhoneCode_label')}
                  name="mobilePhoneCountry"
                  onChange={handleCountryChange('mobilePhone', PhoneType.MOBILEPHONE, onChange)}
                  searchPlaceholder={t('loanApp_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="mobilePhoneFieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!isNewCustomer || !watch('mobilePhoneCountry')}
                    errorMessage={t(errors?.mobilePhoneFieldCode?.message as string, {
                      min: formValidations?.mobilePhone?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.mobilePhone?.maxFieldCodeLength}
                    name="mobilePhoneFieldCode"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="mobilePhoneTelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!isNewCustomer || !watch('mobilePhoneCountry')}
                    errorMessage={t(errors?.mobilePhoneTelephoneNo?.message as string, {
                      min: formValidations?.mobilePhone?.minTelephoneNoLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.mobilePhone?.maxTelephoneNoLength}
                    name="mobilePhoneTelephoneNo"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>
        <Subtitle>
          <Trans i18nKey="loanApp_phoneEmailEntry_livingAddressPhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="actualLivingAddressPhoneCountry"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t(
                    (errors?.actualLivingAddressPhoneCountry as FieldError)?.message as string
                  )}
                  items={countryPhoneCodes}
                  label={t('loanApp_phoneEmailEntry_countryPhoneCode_label')}
                  name="actualLivingAddressPhoneCountry"
                  onChange={handleCountryChange(
                    'actualLivingAddressPhone',
                    PhoneType.HOMEPHONE,
                    onChange
                  )}
                  searchPlaceholder={t('loanApp_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="actualLivingAddressPhoneFieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('actualLivingAddressPhoneCountry')}
                    errorMessage={t(errors.actualLivingAddressPhoneFieldCode?.message as string, {
                      min: formValidations?.actualLivingAddressPhone?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.actualLivingAddressPhone?.maxFieldCodeLength}
                    name="actualLivingAddressPhoneFieldCode"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="actualLivingAddressPhoneTelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('actualLivingAddressPhoneCountry')}
                    errorMessage={t(errors.actualLivingAddressPhoneTelephoneNo?.message as string, {
                      min: formValidations?.actualLivingAddressPhone?.minTelephoneNoLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.actualLivingAddressPhone?.maxTelephoneNoLength}
                    name="actualLivingAddressPhoneTelephoneNo"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="loanApp_phoneEmailEntry_workPhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="workPhoneCountry"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.workPhoneCountry as FieldError)?.message as string)}
                  items={countryPhoneCodes}
                  label={t('loanApp_phoneEmailEntry_countryPhoneCode_label')}
                  name="workPhoneCountry"
                  onChange={handleCountryChange('workPhone', PhoneType.WORKPHONE, onChange)}
                  searchPlaceholder={t('loanApp_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="workPhoneFieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('workPhoneCountry')}
                    errorMessage={t(errors.workPhoneFieldCode?.message as string, {
                      min: formValidations?.workPhone?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.workPhone?.maxFieldCodeLength}
                    name="workPhoneFieldCode"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="workPhoneTelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('workPhoneCountry')}
                    errorMessage={t(errors.workPhoneTelephoneNo?.message as string, {
                      min: formValidations?.workPhone?.minTelephoneNoLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.workPhone?.maxTelephoneNoLength}
                    name="workPhoneTelephoneNo"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="loanApp_phoneEmailEntry_registrationAddressPhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="registrationAddressPhoneCountry"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t(
                    (errors?.registrationAddressPhoneCountry as FieldError)?.message as string
                  )}
                  items={countryPhoneCodes}
                  label={t('loanApp_phoneEmailEntry_countryPhoneCode_label')}
                  name="registrationAddressPhoneCountry"
                  onChange={handleCountryChange(
                    'registrationAddressPhone',
                    PhoneType.REGISTRATIONADDRESSPHONE,
                    onChange
                  )}
                  searchPlaceholder={t('loanApp_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="registrationAddressPhoneFieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('registrationAddressPhoneCountry')}
                    errorMessage={t(errors.registrationAddressPhoneFieldCode?.message as string, {
                      min: formValidations?.registrationAddressPhone?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.workPhone?.maxFieldCodeLength}
                    name="registrationAddressPhoneFieldCode"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="registrationAddressPhoneTelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('workPhoneCountry')}
                    errorMessage={t(errors.registrationAddressPhoneTelephoneNo?.message as string, {
                      min: formValidations?.registrationAddressPhone?.minTelephoneNoLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.registrationAddressPhone?.maxTelephoneNoLength}
                    name="registrationAddressPhoneTelephoneNo"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>

        <Separator />
        <Subtitle>
          <Trans i18nKey="loanApp_phoneEmailEntry_ContactPersonPhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="contactPersonMobilePhone1Country"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t(
                    (errors?.contactPersonMobilePhone1Country as FieldError)?.message as string
                  )}
                  items={countryPhoneCodes}
                  label={t('loanApp_phoneEmailEntry_countryPhoneCode_label')}
                  name="contactPersonMobilePhone1Country"
                  onChange={handleCountryChange(
                    'contactPersonMobilePhone1',
                    PhoneType.CONTACTMOBILEPHONE,
                    onChange
                  )}
                  searchPlaceholder={t('loanApp_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="contactPersonMobilePhone1FieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('contactPersonMobilePhone1Country')}
                    errorMessage={t(errors.contactPersonMobilePhone1FieldCode?.message as string, {
                      min: formValidations?.contactPersonMobilePhone1?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.contactPersonMobilePhone1?.maxFieldCodeLength}
                    name="contactPersonMobilePhone1FieldCode"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="contactPersonMobilePhone1TelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('contactPersonMobilePhone1Country')}
                    errorMessage={t(
                      errors.contactPersonMobilePhone1TelephoneNo?.message as string,
                      {
                        min: formValidations?.contactPersonMobilePhone1?.minTelephoneNoLength,
                      }
                    )}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.contactPersonMobilePhone1?.maxTelephoneNoLength}
                    name="contactPersonMobilePhone1TelephoneNo"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>

        <Separator />
        <Subtitle>
          <Trans i18nKey="loanApp_phoneEmailEntry_ContactPersonPhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="contactPersonMobilePhone2Country"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t(
                    (errors?.contactPersonMobilePhone2Country as FieldError)?.message as string
                  )}
                  items={countryPhoneCodes}
                  label={t('loanApp_phoneEmailEntry_countryPhoneCode_label')}
                  name="contactPersonMobilePhone2Country"
                  onChange={handleCountryChange(
                    'contactPersonMobilePhone2',
                    PhoneType.CONTACTMOBILEPHONE,
                    onChange
                  )}
                  searchPlaceholder={t('loanApp_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="contactPersonMobilePhone2FieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('contactPersonMobilePhone2Country')}
                    errorMessage={t(errors.contactPersonMobilePhone2FieldCode?.message as string, {
                      min: formValidations?.contactPersonMobilePhone2?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.contactPersonMobilePhone2?.maxFieldCodeLength}
                    name="contactPersonMobilePhone2FieldCode"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="contactPersonMobilePhone2TelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('contactPersonMobilePhone2Country')}
                    errorMessage={t(
                      errors.contactPersonMobilePhone2TelephoneNo?.message as string,
                      {
                        min: formValidations?.contactPersonMobilePhone2?.minTelephoneNoLength,
                      }
                    )}
                    formatter={formatNumeric}
                    label={t('loanApp_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.contactPersonMobilePhone2?.maxTelephoneNoLength}
                    name="contactPersonMobilePhone2TelephoneNo"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>

        <Separator />

        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default PhoneEmailEntry;
